import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {StatusChipComponent} from '@aviary/components/status-chip/status-chip.component';
import {MatChipSet} from '@angular/material/chips';
import {DISPLAYABLE_HOLD_STATUSES, Hold, HOLD_STATUS_CHIP_COLORS, HoldStatus} from '@store/holds/holds.types';
import {HoldsSelectors} from '@store/holds/holds.selectors';
import {MxSelectorCreator} from '@aviary/store/store.types';
import {createSelector} from '@ngrx/store';
import {FormatPipe} from '@aviary/pipes/format.pipe';
import {StatusChipColors} from '@aviary/store/common/common.types';

@Component({
  selector: 'px-hold-status-column',
  host: {
    class: 'text-wrap',
  },
  template: `
    <mat-chip-set>
      <mx-status-chip [status]="selectors.statusFor(hold) | select" [displayableStatusNames]="displayableStatusNames" [colors]="chipColors"
                      [subscriptText]="subscriptTextFor(hold) | select" class="text-label-small"/>
    </mat-chip-set>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SelectPipe, StatusChipComponent, MatChipSet]
})
export class HoldStatusColumnComponent {
  @Input({required: true}) public hold: Hold;

  public readonly selectors = HoldsSelectors.Stateless;
  public readonly chipColors: StatusChipColors<HoldStatus> = HOLD_STATUS_CHIP_COLORS;
  public readonly displayableStatusNames: Map<HoldStatus, string> = DISPLAYABLE_HOLD_STATUSES;
  private readonly formatPipe: FormatPipe = inject(FormatPipe);

  public readonly subscriptTextFor: MxSelectorCreator<[Hold], string> =
    hold => createSelector(
      this.selectors.statusFor(hold),
      this.selectors.notWantedBeforeFor(hold),
      (status, notWantedBefore) => {
        /*
           Wondering why 'HELD' and 'REQUESTED' aren't represented here even though Figma specifies subscript text for them?
             - 'HELD' doesn't ever make it into this component because it's in the Ready for Pickup card above the Holds table.
             - 'REQUESTED' doesn't need subscript text because the information it would show is already present in another column in the Holds table.
        */
        return status === 'PAUSED' ? `until ${this.formatPipe.transform(notWantedBefore, 'DATE_ONLY')}` : '';
      }
    );
}
